/**
 * AppShell — Responsive app layout
 *
 * Desktop: sidebar navigation with officer info + quick actions
 * Mobile: bottom tab bar + hamburger menu
 *
 * Theme: Dark blue (#02090A) with blue accents — ported from CopApp 1
 */

import { useEffect, useState, type ReactNode } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSessionStore, useSimulationStore } from '../../stores';
import { prefetchAppRoutes } from '../../prefetch-routes';

const NAV_ITEMS = [
  { path: '/dashboard', label: 'Dashboard', icon: '◉' },
  { path: '/sim', label: 'Simulation', icon: '▶' },
  { path: '/training', label: 'Training', icon: '⬡' },
  { path: '/department', label: 'Department', icon: '◫' },
  { path: '/insights', label: 'Insights', icon: '◈' },
  { path: '/community', label: 'Community', icon: '◎' },
  { path: '/settings', label: 'Settings', icon: '⚙' },
];

// Bottom bar shows the 5 most-used items on mobile
const MOBILE_NAV = NAV_ITEMS.slice(0, 5);

/** Phases that indicate an active simulation is in progress */
const ACTIVE_SIM_PHASES = new Set([
  'active',
  'processing-turn',
  'paused',
  'ready',
  'generating-scenario',
]);

export function AppShell({ children }: { children?: ReactNode }) {
  const { user } = useAuth();
  const { officer } = useSessionStore();
  const phase = useSimulationStore((s) => s.phase);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Hide chrome during active simulation
  const isInSim = location.pathname === '/sim';

  // Idle-prefetch the lazy routes once the shell is up — later taps
  // render from cache instead of flashing a spinner.
  useEffect(() => { prefetchAppRoutes(); }, []);

  // Whether there's an active simulation session (show indicator)
  const hasActiveSim = ACTIVE_SIM_PHASES.has(phase);

  // Officer display name — prefer profile name, fall back to email prefix
  const displayName: string =
    officer?.displayName ||
    (user?.email ? user.email.split('@')[0] || 'Officer' : 'Officer');

  // Initials for avatar
  const initials = displayName
    .split(' ')
    .filter(Boolean)
    .map((w) => w.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2) || 'O';

  const handleNewSimulation = () => {
    navigate('/sim');
  };

  return (
    <div className="flex min-h-screen bg-copapp-dark text-fg">
      {/* Desktop Sidebar — hidden on mobile */}
      <nav className="hidden md:flex w-56 border-r border-copapp-line flex-col shrink-0 bg-copapp-dark">
        {/* Officer identity block */}
        <div className="p-4 border-b border-copapp-line">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-full bg-copapp-700/40 border border-copapp-500/30 flex items-center justify-center text-xs font-semibold text-copapp-300">
              {initials}
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="text-sm font-bold tracking-tight text-fg truncate">
                {displayName}
              </h1>
              <p className="text-[10px] text-fg-muted truncate">
                {[
                  (officer as { rank?: string } | null)?.rank,
                  officer?.role === 'admin' ? 'Admin' : null,
                ].filter(Boolean).join(' · ') || 'Officer'}
                {officer?.departmentId ? ' · Dept' : ' · Solo'}
              </p>
            </div>
          </div>
        </div>

        {/* New Simulation quick-action */}
        <div className="px-3 pt-3 pb-1">
          <button
            onClick={handleNewSimulation}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-copapp-700 hover:bg-copapp-600 text-white text-sm font-medium transition-colors"
          >
            <span className="text-base leading-none">+</span>
            New Simulation
          </button>
        </div>

        {/* Active session indicator */}
        {hasActiveSim && (
          <div className="mx-3 mt-2">
            <NavLink
              to="/sim"
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-copapp-600/10 border border-copapp-500/20 text-copapp-300 text-xs transition-colors hover:bg-copapp-600/20"
            >
              <span className="inline-flex rounded-full h-2 w-2 bg-copapp-400 animate-pulse" />
              Simulation in progress
            </NavLink>
          </div>
        )}

        {/* Nav links */}
        <div className="flex-1 py-2">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                  isActive
                    ? 'text-fg bg-copapp-600/10 border-r-2 border-copapp-500'
                    : 'text-fg-muted hover:text-fg hover:bg-copapp-line/30'
                }`
              }
            >
              <span className="text-xs">{item.icon}</span>
              {item.label}
            </NavLink>
          ))}
        </div>

        <div className="p-4 border-t border-copapp-line" />
      </nav>

      {/* Main content — min-w-0 lets the column shrink below its children's
          min-content width (flex default is min-width:auto, which propagates
          any unshrinkable descendant into page-level horizontal overflow) */}
      <div className="flex-1 min-w-0 flex flex-col min-h-screen">
        {/* Mobile header — hidden on desktop and during sim */}
        {!isInSim && (
          <header className="md:hidden flex items-center justify-between px-4 py-3 border-b border-copapp-line bg-copapp-dark">
            <div className="flex items-center gap-2">
              <h1 className="text-lg font-bold">CopApp</h1>
              {hasActiveSim && (
                <span className="inline-flex rounded-full h-2 w-2 ml-1 bg-copapp-400 animate-pulse" />
              )}
            </div>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="text-fg-muted hover:text-fg p-1"
            >
              {mobileMenuOpen ? '✕' : '☰'}
            </button>
          </header>
        )}

        {/* Mobile menu overlay */}
        {mobileMenuOpen && (
          <div className="md:hidden fixed inset-0 z-40 bg-copapp-dark/95 backdrop-blur-sm pt-14">
            {/* Officer info in mobile menu */}
            <div className="px-4 pb-3 mb-2 border-b border-copapp-line">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-full bg-copapp-700/40 border border-copapp-500/30 flex items-center justify-center text-xs font-semibold text-copapp-300">
                  {initials}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-fg truncate">{displayName}</p>
                  <p className="text-[10px] text-fg-muted truncate">{user?.email}</p>
                </div>
              </div>
            </div>

            {/* New Simulation button in mobile menu */}
            <div className="px-4 pb-3">
              <button
                onClick={() => {
                  handleNewSimulation();
                  setMobileMenuOpen(false);
                }}
                className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg bg-copapp-700 hover:bg-copapp-600 text-white text-sm font-medium transition-colors"
              >
                <span className="text-base leading-none">+</span>
                New Simulation
              </button>
            </div>

            <div className="p-4 space-y-1">
              {NAV_ITEMS.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-3 rounded-lg text-sm transition-colors ${
                      isActive ? 'text-fg bg-copapp-600/20' : 'text-fg-muted'
                    }`
                  }
                >
                  <span>{item.icon}</span>
                  {item.label}
                </NavLink>
              ))}
            </div>
          </div>
        )}

        {/* Page content — children for standalone use, Outlet as layout route */}
        <main className="flex-1 overflow-y-auto pb-16 md:pb-0">
          {children ?? <Outlet />}
        </main>

        {/* Mobile bottom tab bar — hidden on desktop and during sim */}
        {!isInSim && (
          <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-copapp-dark border-t border-copapp-line flex z-30">
            {MOBILE_NAV.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) =>
                  `flex-1 flex flex-col items-center py-2 text-[10px] transition-colors ${
                    isActive ? 'text-copapp-400' : 'text-fg-faint'
                  }`
                }
              >
                <span className="text-sm mb-0.5">{item.icon}</span>
                {item.label}
              </NavLink>
            ))}
          </nav>
        )}
      </div>
    </div>
  );
}
